import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaSignOutAlt, FaUserShield } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import GoogleSignInButton from './GoogleSignInButton';

const SignInPanel = ({ onClose }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  return (
    <div className="text-center py-2">
      <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-primary' : 'text-green-600'}`}>
        {t('auth.signInTitle')}
      </h2>
      <p className={`text-sm mb-6 ${isDarkMode ? 'text-light/70' : 'text-gray-600'}`}>
        {t('auth.signInHint')}
      </p>
      <div className="flex justify-center">
        <GoogleSignInButton onSignedIn={onClose} />
      </div>
      <p className={`text-xs mt-6 ${isDarkMode ? 'text-light/40' : 'text-gray-400'}`}>
        {t('auth.privacy')}
      </p>
    </div>
  );
};

/** Sign-in button when signed out; avatar with a small menu when signed in. */
const UserMenu = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { user, unread, isAdmin, isLoading, signOut } = useAuth();
  const [signInOpen, setSignInOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    const onKeyDown = (event) => { if (event.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  if (isLoading) return <div className="w-9 h-9" aria-hidden="true" />;

  if (!user) {
    return (
      <>
        <button
          type="button"
          onClick={() => setSignInOpen(true)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${isDarkMode
            ? 'bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20'
            : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'}`}
        >
          {t('auth.signIn')}
        </button>
        <Modal isOpen={signInOpen} onClose={() => setSignInOpen(false)}>
          <SignInPanel />
        </Modal>
      </>
    );
  }

  const initials = (user.name || user.email).trim().charAt(0).toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t('auth.account')}
        className="relative flex items-center gap-2"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            referrerPolicy="no-referrer"
            className={`w-9 h-9 rounded-full border-2 ${isDarkMode ? 'border-primary/40' : 'border-green-300'}`}
          />
        ) : (
          <span className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold ${isDarkMode
            ? 'bg-primary/20 text-primary' : 'bg-green-100 text-green-700'}`}
          >
            {initials}
          </span>
        )}
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            role="menu"
            className={`absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-60 rounded-xl overflow-hidden shadow-xl z-50 ${isDarkMode
              ? 'bg-dark border border-primary/20' : 'bg-white border border-gray-200'}`}
          >
            <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-primary/10' : 'border-gray-100'}`}>
              <p className={`font-semibold truncate ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>{user.name}</p>
              <p className={`text-xs truncate ${isDarkMode ? 'text-light/50' : 'text-gray-500'}`}>{user.email}</p>
            </div>

            <Link
              to="/xabarlar"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center justify-between gap-2 px-4 py-2.5 text-sm ${isDarkMode
                ? 'text-light/80 hover:bg-dark-overlay hover:text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="flex items-center gap-2"><FaEnvelope /> {t('auth.myMessages')}</span>
              {unread > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                  {unread}
                </span>
              )}
            </Link>

            {isAdmin && (
              <Link
                to="/admin"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm ${isDarkMode
                  ? 'text-light/80 hover:bg-dark-overlay hover:text-primary' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <FaUserShield /> {t('auth.adminPanel')}
              </Link>
            )}

            <button
              type="button"
              role="menuitem"
              onClick={() => { setMenuOpen(false); signOut(); }}
              className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm border-t ${isDarkMode
                ? 'text-light/80 hover:bg-dark-overlay border-primary/10'
                : 'text-gray-700 hover:bg-gray-50 border-gray-100'}`}
            >
              <FaSignOutAlt className="rtl:rotate-180" /> {t('auth.signOut')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserMenu;
